import { Grid } from '@mantine/core';
import type { UseFormReturn } from 'react-hook-form';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { useSubtypeFormNames } from '../../../../../../react-commons/hooks/useSubtypeFormNames';
import type { FormNames } from '../../../../../../react-commons/utils/form/getFormNames';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { VCycleElementType } from '../../../../resource/enumerations/VCycleElementType';
import {
    VCycleElementPropertyValidation,
    type VCycleElementResource,
    vcycleElementDefaultSubtypeValues,
    vcycleElementSubTypeProperties,
} from '../../../../resource/resources/artifacts/use_case/representation/vcycleElementResource';
import { PropertyInputs } from '../../../reusable-components/base/property-bases/PropertyInputs';
import { ReferenceInputs } from '../../../reusable-components/base/reference-text-fields/ReferenceInputs';
import type { PropertyValidationInformation } from '../../../util/PropertyValidationInformation';
import { VCycleElementDecoratorDisplay } from './VCycleElementDecoratorDisplay';

export const VCycleElementContent = ({
    id,
    projectId,
    projectVersionId,
    form,
    formNames,
    readOnly,
}: {
    id?: string;
    projectId: string | undefined | null;
    projectVersionId: string | undefined | null;
    form: UseFormReturn<VCycleElementResource>;
    formNames: FormNames<VCycleElementResource>;
    readOnly: boolean;
}) => {
    const subtypeFormNames = useSubtypeFormNames({
        form,
        typeName: formNames.type,
        subtypeProperties: vcycleElementSubTypeProperties,
        defaultSubtypeValues: vcycleElementDefaultSubtypeValues,
    });

    const getPropertyValidationInformation = (
        propertyIdentifier: VCycleElementPropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: id,
        resourceType: ResourceType.V_CYCLE_ELEMENT,
        propertyIdentifier,
    });

    return (
        <Grid>
            <Grid.Col span={6}>
                <PropertyInputs.Enum
                    name={formNames.type}
                    control={form.control}
                    enumType={VCycleElementType}
                    nullable={false}
                    readOnly={true}
                    propertyValidationInformation={getPropertyValidationInformation(
                        VCycleElementPropertyValidation.TYPE
                    )}
                />
            </Grid.Col>
            {subtypeFormNames.representation && (
                <Grid.Col span={6}>
                    <ReferenceInputs.WorkflowRepresentation
                        name={subtypeFormNames.representation}
                        nullable={false}
                        form={form}
                        projectVersionId={projectVersionId}
                        projectId={projectId}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(
                            VCycleElementPropertyValidation.REPRESENTATION
                        )}
                    />
                </Grid.Col>
            )}
            {subtypeFormNames.processArea && (
                <Grid.Col span={6}>
                    <ReferenceInputs.ProcessArea
                        name={subtypeFormNames.processArea}
                        nullable={false}
                        form={form}
                        projectVersionId={projectVersionId}
                        projectId={projectId}
                        readOnly={readOnly}
                        propertyValidationInformation={getPropertyValidationInformation(
                            VCycleElementPropertyValidation.PROCESS_AREA
                        )}
                    />
                </Grid.Col>
            )}
            <Show when={id}>
                {(parentId) => (
                    <Grid.Col span={12}>
                        <VCycleElementDecoratorDisplay parentId={parentId} />
                    </Grid.Col>
                )}
            </Show>
        </Grid>
    );
};
